// @ts-strict-ignore
import * as React from 'react';
import { IntegrationCapability, IntegrationType } from '../types';
import { AppComponentProps, AppRouteComponentMatchParams, StringKeyedDictionary } from '../typescriptHelpers';
import { CreateIntegrationRequestDataType } from '../actions/integrations';
import ConnectIntegrationModal from './ConnectIntegrationModal';
import { useParams } from 'react-router-dom';
import {
    selectAppPlatform,
    selectAppStoreType,
    selectOrgProviders,
    selectIsRunwayOrg,
    selectWorkflowEnabledSteps,
} from '../selectors/selectors';
import { useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';
import List from 'antd/lib/list';
import IntegrationProviderModal from './IntegrationProviderModal';
import { ConnectButtonForIntegrationType } from './ConnectButtonForIntegrationType';
import { allComingSoonIntegrations, canInstallMultipleIntegrationsForCapability } from '../helpers';
import ConnectAvailableIntegrationComponent from './appSettings/ConnectAvailableIntegrationComponent';
import ComingSoonIntegrationComponent from './appSettings/ComingSoonIntegrationComponent';

interface IntegrationsForCapabilityComponentProps extends AppComponentProps {
    integrationCapability: IntegrationCapability;
    isDisabled?: boolean;
    shouldUseSmallGrid?: boolean;
    allSupportedIntegrationsForCapability: IntegrationType[];
    connectedIntegrationsForCapability: IntegrationType[];
    integrationIsLoading: StringKeyedDictionary<boolean>; // dictionary of integrationType to boolean
    addIntegrationSuccess: StringKeyedDictionary<boolean>;
    addIntegration: (integrationType: IntegrationType, data: CreateIntegrationRequestDataType) => void;
}

const IntegrationsForCapabilityComponent: React.FC<IntegrationsForCapabilityComponentProps> = (
    props: IntegrationsForCapabilityComponentProps,
) => {
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();
    const [showIntegrationFieldsForm, setShowIntegrationFieldsForm] = React.useState(false);
    const [integrationType, setIntegrationType] = React.useState(null);
    const [providerIntegrationType, setProviderIntegrationType] = React.useState(null);
    const [showProviderModal, setShowProviderModal] = React.useState(false);

    const integrationProviders = useSelector((state: RootState) => selectOrgProviders(state, props.orgId));

    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, props.appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));

    const onClickIntegrationShowInstallationModal = (integrationType: IntegrationType) => {
        setIntegrationType(integrationType);
        setShowIntegrationFieldsForm(true);
    };

    const onClickShowProviderModal = (integrationType: IntegrationType) => {
        setProviderIntegrationType(integrationType);
        setShowProviderModal(true);
    };

    const onClickInstallRunwayIntegration = (integrationType: IntegrationType) => {
        props.addIntegration(integrationType, {});
    };

    const isRunwayOrg = useSelector((state: RootState) => selectIsRunwayOrg(state));
    const integrationsComingSoon = allComingSoonIntegrations(isRunwayOrg, props.orgId);

    const connectIntegrationIsLoading = integrationType ? props.integrationIsLoading[integrationType] : false;
    const providerConnectIntegrationIsLoading =
        providerIntegrationType ? props.integrationIsLoading[providerIntegrationType] : false;

    const workflowsMigratedSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, props.appId));

    // Build Distro for beta is hidden from available integrations; filter it out rather than rendering an
    // empty tile, which would still occupy a grid cell.
    const availableIntegrationTypes = props.allSupportedIntegrationsForCapability.filter(
        (integrationType) => integrationType !== IntegrationType.RunwayBuildDistroBeta,
    );

    const renderIntegrationTileForIntegrationType = (integrationType: IntegrationType) => {
        const isComingSoonIntegration = integrationsComingSoon.includes(integrationType);
        const hasAtLeastOneIntegrationForCapabilityConnected = props.connectedIntegrationsForCapability.length > 0;
        const canInstallMultipleIntegrationsForCapabilityValue = canInstallMultipleIntegrationsForCapability(
            props.integrationCapability,
            workflowsMigratedSteps,
        );
        const disableMultipleConnections =
            hasAtLeastOneIntegrationForCapabilityConnected && !canInstallMultipleIntegrationsForCapabilityValue;

        const isDisabled = props.isDisabled || disableMultipleConnections;

        if (isComingSoonIntegration) {
            return (
                <ComingSoonIntegrationComponent
                    orgId={props.orgId}
                    appId={props.appId}
                    integrationType={integrationType}
                />
            );
        } else {
            const isLoading = props.integrationIsLoading[integrationType] ?? false;
            const connectButton = (
                <ConnectButtonForIntegrationType
                    integrationType={integrationType}
                    appPlatform={appPlatform}
                    appStoreType={appStoreType}
                    orgId={props.orgId}
                    orgIdParam={orgIdParam}
                    appIdParam={appIdParam}
                    disabled={isDisabled}
                    integrationProviders={integrationProviders}
                    onClickShowInstallationModal={onClickIntegrationShowInstallationModal}
                    onClickInstallRunwayIntegration={onClickInstallRunwayIntegration}
                    onClickShowProviderModal={onClickShowProviderModal}
                    canInstallMultipleIntegrationsForCapability={canInstallMultipleIntegrationsForCapabilityValue}
                />
            );

            return (
                <ConnectAvailableIntegrationComponent
                    integrationType={integrationType}
                    connectButton={connectButton}
                    isLoading={isLoading}
                    shouldUseSmallGrid={props.shouldUseSmallGrid}
                />
            );
        }
    };

    React.useEffect(() => {
        if (showProviderModal && providerIntegrationType) {
            for (const intId in props.addIntegrationSuccess) {
                // split installation ID by :, the first part is the type
                const integrationType = intId.split(':')[0];
                if (integrationType === providerIntegrationType && props.addIntegrationSuccess[intId]) {
                    // integration successfully added for existing provider, hide modal
                    setShowProviderModal(false);
                    setProviderIntegrationType(null);
                }
            }
        }

        for (const intId in props.addIntegrationSuccess) {
            // split installation ID by :, the first part is the type
            if (intId.split(':')[0] === integrationType && props.addIntegrationSuccess[intId]) {
                // integration successfully added for new provider, hide modal
                setShowIntegrationFieldsForm(false);
            }
        }
    }, [props.addIntegrationSuccess]);

    return (
        <div>
            <ConnectIntegrationModal
                orgId={props.orgId}
                appId={props.appId}
                isLoading={connectIntegrationIsLoading}
                integrationType={integrationType}
                open={showIntegrationFieldsForm}
                addIntegration={props.addIntegration}
                onCancel={() => {
                    setShowIntegrationFieldsForm(false);
                }}
            />
            <IntegrationProviderModal
                orgId={props.orgId}
                isLoading={providerConnectIntegrationIsLoading}
                integrationType={providerIntegrationType}
                open={showProviderModal}
                addIntegration={props.addIntegration}
                onCancel={() => {
                    setShowProviderModal(false);
                }}
                onConnectNewProvider={(integrationType) => {
                    setShowProviderModal(false);
                    onClickIntegrationShowInstallationModal(integrationType);
                }}
            />
            <List
                grid={{
                    gutter: 24,
                    xs: 1,
                    sm: props.shouldUseSmallGrid ? 2 : 2,
                    md: props.shouldUseSmallGrid ? 2 : 2,
                    lg: props.shouldUseSmallGrid ? 2 : 3,
                    xl: props.shouldUseSmallGrid ? 2 : 4,
                    xxl: props.shouldUseSmallGrid ? 2 : 4,
                }}
                dataSource={availableIntegrationTypes}
                renderItem={(integrationType: IntegrationType) => {
                    return (
                        <List.Item key={integrationType}>
                            {renderIntegrationTileForIntegrationType(integrationType)}
                        </List.Item>
                    );
                }}
            />
        </div>
    );
};

export default IntegrationsForCapabilityComponent;
